import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ShieldCheck, Moon, Sun } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all ${
        scrolled ? "glass-nav shadow-[0_2px_20px_-8px_rgba(11,18,32,0.15)]" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="container-page flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight" data-testid="nav-logo">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-[#1565FF] to-[#4FC3F7] text-white shadow-sm">
            <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <span className="text-[15px]">Tech Alpha <span className="text-[#1565FF]">SSL</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm transition-colors ${
                  isActive
                    ? "text-[#1565FF] dark:text-[#4FC3F7] bg-[#1565FF]/5 dark:bg-white/5"
                    : "text-foreground/70 hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/portal/login" className="hidden md:inline-flex btn-ghost !py-2 !px-4 !text-sm" data-testid="nav-portal-login">
            Client Portal
          </Link>
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="theme-toggle"
            className="hidden md:grid h-9 w-9 place-items-center rounded-full border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 transition"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link to="/quote" className="hidden md:inline-flex btn-primary !py-2 !px-4 !text-sm" data-testid="nav-cta-quote">
            Request Quote
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="md:hidden grid h-9 w-9 place-items-center rounded-full border border-black/10 dark:border-white/10"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 dark:border-white/10 glass-nav">
          <div className="container-page py-3 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm ${
                    isActive ? "text-[#1565FF] bg-[#1565FF]/5 dark:text-[#4FC3F7] dark:bg-white/5" : "text-foreground/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <button onClick={toggle} className="btn-ghost !py-2 !px-3 flex-1" data-testid="mobile-theme-toggle">
                {theme === "dark" ? <><Sun className="h-4 w-4"/> Light</> : <><Moon className="h-4 w-4"/> Dark</>}
              </button>
              <Link to="/quote" className="btn-primary !py-2 !px-3 flex-1" data-testid="mobile-cta-quote">Quote</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
